import React, { useEffect, useMemo, useState, useRef } from "react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import {
    DownloadSimple, UploadSimple, X, Gear, Info, ArrowsClockwise, Path,
    Warning, ChartBar, Truck, Camera, Image as ImageIcon, ListChecks, MagnifyingGlass,
    Pause, Play, WarningOctagon, ClockCounterClockwise, Aperture,
} from "@phosphor-icons/react";
import LivePlateScanner from "./LivePlateScanner";
import MainDashboard from "./tronox/MainDashboard";

/* ═══════════════════════════════════════════════════════════════════════════
 * Tronox (GTRB) Silo Storage — main dashboard
 *
 * Always-active view of all 9 silos with live fill levels, per-silo product,
 * inbound receive + outbound export actions, and an admin capacity editor.
 * Mirrors the vessel-loading UX (feature parity coming in phase 2 — export
 * flow, delays, corrections, certificate).
 * ═══════════════════════════════════════════════════════════════════════════ */

const NUM = (n) => (Number(n) || 0).toLocaleString(undefined, { maximumFractionDigits: 2 });

function fmtDT() {
    const d = new Date();
    return d.toLocaleString(undefined, { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", second: "2-digit" });
}

const PRODUCT_TEXT = {
    ZIRCON: "text-emerald-400",
    RUTILE: "text-amber-400",
    CP:     "text-teal-300",
    SP:     "text-purple-400",
};

// SVG silo — realistic industrial silo with cylindrical body, conical bottom,
// ladder up the side, filled with product colour to the fill percentage.
function SiloSVG({ colour = "#22C55E", fillPct = 0 }) {
    const clamped = Math.max(0, Math.min(100, fillPct));
    // Body vertical extent (SVG y: 30 -> 220 = body top to bottom)
    const bodyTop = 30, bodyBottom = 220, bodyHeight = bodyBottom - bodyTop;
    const fillY = bodyBottom - (bodyHeight * clamped / 100);
    return (
        <svg viewBox="0 0 130 260" width="110" height="220" className="drop-shadow-xl" role="img">
            <defs>
                <linearGradient id="siloBody" x1="0" y1="0" x2="1" y2="0">
                    <stop offset="0%"   stopColor="#8B9299" />
                    <stop offset="50%"  stopColor="#C9CFD6" />
                    <stop offset="100%" stopColor="#6B7280" />
                </linearGradient>
                <linearGradient id="siloProduct" x1="0" y1="0" x2="1" y2="0">
                    <stop offset="0%"   stopColor={colour} stopOpacity="0.85" />
                    <stop offset="50%"  stopColor={colour} stopOpacity="1" />
                    <stop offset="100%" stopColor={colour} stopOpacity="0.75" />
                </linearGradient>
                <clipPath id="siloClip">
                    {/* Cylinder body + cone bottom, matches the outer silhouette */}
                    <path d="M 25,30 L 105,30 L 105,220 L 90,250 L 40,250 L 25,220 Z" />
                </clipPath>
            </defs>

            {/* Roof */}
            <path d="M 22,32 Q 65,10 108,32 L 105,34 L 25,34 Z" fill="#4B5563" stroke="#1F2937" strokeWidth="0.5" />
            {/* Silo body */}
            <rect x="25" y="30" width="80" height="190" fill="url(#siloBody)" stroke="#374151" strokeWidth="0.5" />
            {/* Cone bottom */}
            <path d="M 25,220 L 105,220 L 90,250 L 40,250 Z" fill="url(#siloBody)" stroke="#374151" strokeWidth="0.5" />
            {/* Product fill (clipped to silo silhouette) */}
            <g clipPath="url(#siloClip)">
                <rect x="0" y={fillY} width="130" height={260 - fillY} fill="url(#siloProduct)" />
                {/* Subtle "top of pile" line */}
                <line x1="25" y1={fillY} x2="105" y2={fillY} stroke="#ffffff" strokeOpacity="0.35" strokeWidth="1" />
            </g>
            {/* Horizontal stiffener rings on body */}
            {[60, 100, 140, 180].map((y) => (
                <line key={y} x1="25" y1={y} x2="105" y2={y} stroke="#374151" strokeOpacity="0.4" strokeWidth="0.4" />
            ))}
            {/* Ladder on the left */}
            <line x1="21" y1="34" x2="21" y2="220" stroke="#111827" strokeWidth="1.4" />
            <line x1="25" y1="34" x2="25" y2="220" stroke="#111827" strokeWidth="0.4" />
            {[45, 65, 85, 105, 125, 145, 165, 185, 205].map((y) => (
                <line key={y} x1="21" y1={y} x2="25" y2={y} stroke="#111827" strokeWidth="0.8" />
            ))}
            {/* Support legs */}
            <line x1="40" y1="250" x2="42" y2="258" stroke="#1F2937" strokeWidth="2" />
            <line x1="90" y1="250" x2="88" y2="258" stroke="#1F2937" strokeWidth="2" />
        </svg>
    );
}

/* Silo operational status — the ONLY thing everyone sees at a glance on the main dashboard.
 * Priority: PAUSED > EXPORTING > RECEIVING > IDLE. Everything is read-only info; all
 * pause/delay/close actions live inside the truck-delivery window on the row itself. */
function SiloStatusPill({ silo }) {
    const open = silo.open_deliveries || [];
    const anyPaused = open.some((d) => d.pause_state);
    if (silo.pause_state) {
        return (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-rose-900/60 text-rose-100 border border-rose-500/60" data-testid={`silo-status-${silo.silo_no}`}>
                <WarningOctagon size={12} weight="fill" /> Silo halted
            </span>
        );
    }
    if (open.length > 0) {
        return (
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${anyPaused ? "bg-amber-900/60 text-amber-100 border border-amber-500/60" : "bg-emerald-900/60 text-emerald-100 border border-emerald-500/60"}`} data-testid={`silo-status-${silo.silo_no}`}>
                <span className={`inline-block w-1.5 h-1.5 rounded-full ${anyPaused ? "bg-amber-300" : "bg-emerald-300 animate-pulse"}`} />
                {anyPaused
                    ? `Paused · ${open.length} truck${open.length === 1 ? "" : "s"}`
                    : `Receiving · ${open.length} truck${open.length === 1 ? "" : "s"}`}
            </span>
        );
    }
    // TODO — when export flow lands, add exporting state here
    return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-slate-800 text-slate-400 border border-slate-600" data-testid={`silo-status-${silo.silo_no}`}>
            <span className="inline-block w-1.5 h-1.5 rounded-full bg-slate-500" /> Idle
        </span>
    );
}


/* Live-updating elapsed-minute counter starting from an ISO timestamp.
 * Pauses when `paused` is true so the "productive" clock stops during a pause. */
function useLiveElapsedMinutes(arrivedIso, pausedIso, totalPauseMinutes) {
    const [now, setNow] = useState(() => new Date());
    useEffect(() => {
        const t = setInterval(() => setNow(new Date()), 15000);
        return () => clearInterval(t);
    }, []);
    if (!arrivedIso) return { total: 0, productive: 0, currentPause: 0 };
    const arrivedMs = new Date(arrivedIso).getTime();
    const totalMin = Math.max(0, (now.getTime() - arrivedMs) / 60000);
    let currentPause = 0;
    if (pausedIso) currentPause = Math.max(0, (now.getTime() - new Date(pausedIso).getTime()) / 60000);
    const productive = Math.max(0, totalMin - (Number(totalPauseMinutes) || 0) - currentPause);
    return { total: totalMin, productive, currentPause };
}


function fmtMinutes(min) {
    const m = Math.floor(min);
    const h = Math.floor(m / 60);
    const mm = m % 60;
    if (h > 0) return `${h}h ${mm}m`;
    return `${m}m`;
}


function TruckWindowRow({ delivery, onPause, onResume, onClose, canOperate }) {
    const paused = !!delivery.pause_state;
    const { total, productive } = useLiveElapsedMinutes(
        delivery.arrived_at,
        paused ? delivery.pause_state.started_at : null,
        delivery.total_pause_minutes || 0,
    );
    const stateColor = paused ? "border-amber-500/60 bg-amber-900/20" : "border-emerald-500/40 bg-emerald-900/20";
    return (
        <div className={`rounded border ${stateColor} px-2 py-1.5`} data-testid={`truck-window-${delivery.id}`}>
            <div className="flex items-center gap-2 text-[11px]">
                <Truck size={14} className={paused ? "text-amber-300" : "text-emerald-300"} weight="bold" />
                <span className="font-mono font-bold text-white">{delivery.truck_registration}</span>
                <span className="text-slate-400">·</span>
                <span className="text-slate-300">IN <b>{NUM(delivery.slip_in_tons)} t</b></span>
                <span className="ml-auto flex items-center gap-1 text-slate-300">
                    <span className={`inline-block w-1.5 h-1.5 rounded-full ${paused ? "bg-amber-400" : "bg-emerald-400 animate-pulse"}`} />
                    <span className="font-mono">{fmtMinutes(total)}</span>
                    {(delivery.total_pause_minutes || 0) > 0 && (
                        <span className="text-slate-500 text-[10px]">({fmtMinutes(productive)} prod)</span>
                    )}
                </span>
            </div>
            {paused && (
                <div className="mt-1 text-[10px] text-amber-200 flex items-center gap-1">
                    <WarningOctagon size={11} weight="fill" className="text-amber-400" />
                    <span className="truncate">Paused · {delivery.pause_state.reason}{delivery.pause_state.eta_minutes ? ` · ETA ${delivery.pause_state.eta_minutes}m` : ""}</span>
                </div>
            )}
            {canOperate && (
                <div className="mt-1 flex gap-1">
                    {paused ? (
                        <button onClick={onResume} className="flex-1 inline-flex items-center justify-center gap-1 rounded bg-emerald-700 hover:bg-emerald-600 text-white text-[10px] font-bold py-0.5" data-testid={`truck-resume-${delivery.id}`}>
                            <Play size={11} weight="bold" /> Resume
                        </button>
                    ) : (
                        <button onClick={onPause} className="flex-1 inline-flex items-center justify-center gap-1 rounded bg-amber-700 hover:bg-amber-600 text-white text-[10px] font-bold py-0.5" data-testid={`truck-pause-${delivery.id}`}>
                            <Pause size={11} weight="bold" /> Pause
                        </button>
                    )}
                    <button onClick={onClose} disabled={paused} className="flex-1 inline-flex items-center justify-center gap-1 rounded bg-sky-700 hover:bg-sky-600 disabled:bg-slate-700 disabled:text-slate-500 text-white text-[10px] font-bold py-0.5" data-testid={`truck-close-${delivery.id}`}>
                        <UploadSimple size={11} weight="bold" /> Weigh out & close
                    </button>
                </div>
            )}
        </div>
    );
}


function SiloCard({ silo, onReceive, onConfigure, onDeliveryPause, onDeliveryResume, onDeliveryClose, onDeliveryDelay, canConfigure, canOperate }) {
    const productClass = PRODUCT_TEXT[silo.product] || "text-slate-300";
    const routeString = silo.inbound_route.join(" → ");
    return (
        <div
            data-testid={`tronox-silo-card-${silo.silo_no}`}
            className="rounded-xl bg-slate-800/70 border border-slate-700 p-4 flex gap-4 items-stretch shadow-lg hover:border-slate-500 transition-colors"
        >
            <div className="flex-shrink-0">
                <SiloSVG colour={silo.colour} fillPct={silo.fill_pct} />
            </div>
            <div className="flex-1 min-w-0 flex flex-col">
                <div className="flex items-center justify-between">
                    <div>
                        <div className="text-2xl font-black tracking-tight text-white" data-testid={`silo-title-${silo.silo_no}`}>
                            SILO {silo.silo_no}
                        </div>
                        <div className={`text-sm font-bold tracking-wider ${productClass}`} data-testid={`silo-product-${silo.silo_no}`}>
                            {silo.product}
                        </div>
                    </div>
                    <div className="flex flex-col items-end gap-1">
                        <SiloStatusPill silo={silo} />
                        {canConfigure && (
                            <button
                                onClick={() => onConfigure(silo)}
                                className="inline-flex items-center gap-1 px-2 py-1 rounded text-[10px] uppercase tracking-wider text-slate-300 border border-slate-600 hover:border-slate-400 hover:text-white transition-colors"
                                title="Set capacity + current tons"
                                data-testid={`silo-configure-btn-${silo.silo_no}`}
                            >
                                <Gear size={12} /> Configure
                            </button>
                        )}
                    </div>
                </div>

                {/* Fill level bar */}
                <div className="mt-2">
                    <div className="flex items-center justify-between text-[11px] uppercase tracking-wider text-slate-400">
                        <span>Fill level</span>
                        <span className="text-white text-lg font-bold" data-testid={`silo-fill-pct-${silo.silo_no}`}>
                            {silo.fill_pct.toFixed(1)}%
                        </span>
                    </div>
                    <div className="h-2 rounded bg-slate-900 overflow-hidden mt-1">
                        <div
                            data-testid={`silo-fill-bar-${silo.silo_no}`}
                            className="h-full rounded transition-all"
                            style={{ width: `${silo.fill_pct}%`, background: silo.colour }}
                        />
                    </div>
                    <div className="mt-1 flex justify-between text-[11px] text-slate-500">
                        <span>{NUM(silo.current_level_tons)} t</span>
                        <span>Cap {NUM(silo.capacity_tons)} t</span>
                    </div>
                </div>

                {/* Action buttons + counters */}
                <div className="mt-3 grid grid-cols-2 gap-2">
                    <div className="rounded-lg bg-slate-900/60 border border-slate-700 p-2">
                        <div className="text-[10px] uppercase text-slate-400 tracking-wider">Inbound receiving</div>
                        <button
                            disabled={!canOperate}
                            onClick={() => onReceive(silo)}
                            className="w-full mt-1 flex items-center justify-center gap-1 rounded bg-emerald-600 hover:bg-emerald-500 disabled:bg-slate-700 disabled:text-slate-500 text-white text-sm font-bold py-1.5 transition-colors"
                            data-testid={`silo-receive-btn-${silo.silo_no}`}
                        >
                            <Truck size={14} weight="bold" /> TRUCK IN
                        </button>
                        <div className="text-[10px] uppercase text-slate-500 mt-1">Tonnage received</div>
                        <div className="text-emerald-400 font-bold text-sm" data-testid={`silo-tonnage-received-${silo.silo_no}`}>
                            {NUM(silo.tonnage_received_lifetime)} t
                        </div>
                    </div>
                    <div className="rounded-lg bg-slate-900/60 border border-slate-700 p-2">
                        <div className="text-[10px] uppercase text-slate-400 tracking-wider">Outbound export</div>
                        <div className="mt-1 text-[11px] text-slate-500">
                            Managed on the Outbound tab
                        </div>
                        <div className="text-[10px] uppercase text-slate-500 mt-1">Tonnage exported</div>
                        <div className="text-sky-300 font-bold text-sm" data-testid={`silo-tonnage-exported-${silo.silo_no}`}>
                            {NUM(silo.tonnage_exported_lifetime)} t
                        </div>
                    </div>
                </div>

                {/* Trucks on site — live delivery windows */}
                {silo.open_deliveries && silo.open_deliveries.length > 0 && (
                    <div className="mt-2 space-y-1" data-testid={`silo-open-deliveries-${silo.silo_no}`}>
                        {silo.open_deliveries.map((od) => (
                            <TruckWindowRow
                                key={od.id}
                                delivery={od}
                                onPause={() => onDeliveryPause(od)}
                                onResume={() => onDeliveryResume(od)}
                                onClose={() => onDeliveryClose(od)}
                                canOperate={canOperate}
                            />
                        ))}
                    </div>
                )}

                {/* Paused pill — kept for visibility only. Silo-wide pause is now managed elsewhere (admin-only). */}
                {silo.pause_state && (
                    <div className="mt-2 flex items-start gap-2 text-[11px] text-amber-200 bg-amber-900/30 border border-amber-600/50 rounded px-2 py-1" data-testid={`silo-paused-${silo.silo_no}`}>
                        <WarningOctagon size={14} weight="fill" className="mt-[1px] text-amber-400 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                            <div className="font-bold truncate">PAUSED · {silo.pause_state.reason}</div>
                            <div className="text-amber-300/80 truncate">
                                {silo.pause_state.description || "—"}
                                {silo.pause_state.eta_minutes != null && (
                                    <span className="ml-1">· ETA {silo.pause_state.eta_minutes} min</span>
                                )}
                                {silo.pause_state.accountability === "grindrod" && (
                                    <span className="ml-1 inline-block px-1 rounded bg-rose-900/50 text-rose-200 border border-rose-700">Millwright alerted</span>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {/* Route hint */}
                <div className="mt-2 flex items-start gap-1 text-[10px] text-slate-500" title={routeString}>
                    <Path size={12} className="mt-[2px] flex-shrink-0" />
                    <span className="truncate" data-testid={`silo-route-${silo.silo_no}`}>{routeString}</span>
                </div>
            </div>
        </div>
    );
}


function ReceiveModal({ silo, onClose, onSuccess }) {
    const [source, setSource] = useState("");
    const [expected, setExpected] = useState("");
    const [notes, setNotes] = useState("");
    const [busy, setBusy] = useState(false);

    const submit = async () => {
        if (!source.trim()) return toast.error("Please enter a source (mine, fleet, wagon…)");
        setBusy(true);
        try {
            await api.post("/tronox/inbound/start", {
                silo_no: silo.silo_no,
                product: silo.product,
                source: source.trim(),
                expected_tons: parseFloat(expected) || 0,
                notes: notes.trim(),
            });
            toast.success(`Inbound started for Silo ${silo.silo_no}`);
            onSuccess();
        } catch (e) {
            toast.error(formatApiError(e));
        }
        setBusy(false);
    };

    return (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-3" data-testid="receive-modal">
            <div className="bg-white rounded-lg max-w-md w-full p-5 shadow-2xl">
                <div className="flex items-start justify-between mb-3">
                    <div>
                        <div className="text-xs uppercase text-slate-500 tracking-wider">Start inbound receiving</div>
                        <h2 className="text-lg font-black text-slate-900">Silo {silo.silo_no} · {silo.product}</h2>
                    </div>
                    <button onClick={onClose} className="text-slate-400 hover:text-slate-800" data-testid="receive-modal-close"><X size={20} /></button>
                </div>
                <div className="text-xs text-slate-600 border border-dashed border-slate-300 rounded p-2 mb-3">
                    <div className="font-bold text-slate-700 mb-1">Inbound route</div>
                    <div>{silo.inbound_route.join(" → ")}</div>
                </div>
                <label className="block text-xs font-bold uppercase text-slate-600 mb-1">Source</label>
                <input value={source} onChange={(e) => setSource(e.target.value)} placeholder="e.g. Zircon Rail Wagon 12, Truck Fleet A" className="g-input w-full mb-3" data-testid="receive-source" />
                <label className="block text-xs font-bold uppercase text-slate-600 mb-1">Expected tons (optional)</label>
                <input type="number" value={expected} onChange={(e) => setExpected(e.target.value)} placeholder="e.g. 3000" className="g-input w-full mb-3" data-testid="receive-expected" />
                <label className="block text-xs font-bold uppercase text-slate-600 mb-1">Notes</label>
                <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} placeholder="Optional notes" className="g-input w-full mb-3" data-testid="receive-notes" />
                <div className="flex gap-2 justify-end">
                    <button onClick={onClose} className="btn-outline text-sm" data-testid="receive-cancel" disabled={busy}>Cancel</button>
                    <button onClick={submit} disabled={busy} className="btn-primary text-sm bg-emerald-600 hover:bg-emerald-500" data-testid="receive-start">
                        {busy ? "Starting…" : "Start receiving"}
                    </button>
                </div>
            </div>
        </div>
    );
}


function LogInboundModal() { return null; }  // deprecated — replaced by TruckDeliveryModal


function ConfigureSiloModal({ silo, onClose, onSuccess }) {
    const [cap, setCap] = useState(silo.capacity_tons);
    const [level, setLevel] = useState(silo.current_level_tons);
    const [reason, setReason] = useState("");
    const [busy, setBusy] = useState(false);

    const submit = async () => {
        const c = parseFloat(cap);
        const l = parseFloat(level);
        if (!c || c <= 0) return toast.error("Capacity must be > 0");
        if (l < 0) return toast.error("Current tons cannot be negative");
        if (l > c) return toast.error("Current tons cannot exceed capacity");
        if (!reason.trim()) return toast.error("Please give a reason (audit trail)");
        setBusy(true);
        try {
            await api.post(`/tronox/silos/${silo.silo_no}/configure`, {
                capacity_tons: c,
                current_level_tons: l,
                reason: reason.trim(),
            });
            toast.success(`Silo ${silo.silo_no} configured — cap ${NUM(c)} t · current ${NUM(l)} t`);
            onSuccess();
        } catch (e) { toast.error(formatApiError(e)); }
        setBusy(false);
    };

    return (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-3" data-testid="configure-modal">
            <div className="bg-white rounded-lg max-w-md w-full p-5 shadow-2xl">
                <div className="flex items-start justify-between mb-3">
                    <div>
                        <div className="text-xs uppercase text-slate-500 tracking-wider">Configure silo</div>
                        <h2 className="text-lg font-black text-slate-900">Silo {silo.silo_no} · {silo.product}</h2>
                    </div>
                    <button onClick={onClose} className="text-slate-400 hover:text-slate-800" data-testid="configure-close"><X size={20} /></button>
                </div>
                <div className="text-xs text-slate-600 border border-dashed border-slate-300 rounded p-2 mb-3">
                    Set the actual <b>design capacity</b> and any <b>opening stock</b> already sitting in the silo. Every change is logged for audit.
                </div>
                <label className="block text-xs font-bold uppercase text-slate-600 mb-1">Design capacity (tons)</label>
                <input type="number" value={cap} onChange={(e) => setCap(e.target.value)} className="g-input w-full mb-3" data-testid="configure-capacity" autoFocus />
                <label className="block text-xs font-bold uppercase text-slate-600 mb-1">Current tons in silo</label>
                <input type="number" step="0.01" value={level} onChange={(e) => setLevel(e.target.value)} className="g-input w-full mb-3" data-testid="configure-level" />
                <label className="block text-xs font-bold uppercase text-slate-600 mb-1">Reason</label>
                <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Commissioning baseline / Monthly reconciliation" className="g-input w-full mb-3" data-testid="configure-reason" />
                <div className="flex gap-2 justify-end">
                    <button onClick={onClose} className="btn-outline text-sm" data-testid="configure-cancel" disabled={busy}>Cancel</button>
                    <button onClick={submit} disabled={busy} className="btn-primary text-sm" data-testid="configure-confirm">{busy ? "Saving…" : "Confirm setup"}</button>
                </div>
            </div>
        </div>
    );
}


/* Resize an image file client-side to keep payloads AND peak memory tiny — critical on
 * low-memory Android phones (Chrome tab crashes with "Unable to complete previous
 * operation due to low memory" when handling raw 10-12 MB camera JPEGs).
 *
 * Strategy (in preference order):
 *   1. Try `createImageBitmap(file, { resizeWidth, resizeHeight })` — the browser
 *      decodes and resizes off the main thread with a fraction of the memory of a
 *      full DataURL read.
 *   2. Fall back to `URL.createObjectURL(file)` + `<img>` — still avoids the
 *      15-20 MB data-URL string that the old FileReader approach produced.
 * Never uses `FileReader.readAsDataURL(file)` on the raw camera photo.
 */
async function resizeImageToBase64(file, maxDim = 1280, quality = 0.7) {
    const drawToCanvas = (source, w, h) => {
        const canvas = document.createElement("canvas");
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(source, 0, 0, w, h);
        const dataUrl = canvas.toDataURL("image/jpeg", quality);
        // Free the canvas immediately
        canvas.width = 0;
        canvas.height = 0;
        return dataUrl;
    };

    // Prefer createImageBitmap — off-main-thread decode with built-in resize
    if (typeof window.createImageBitmap === "function") {
        try {
            const probe = await window.createImageBitmap(file);
            const scale = Math.min(1, maxDim / Math.max(probe.width, probe.height));
            const w = Math.round(probe.width * scale);
            const h = Math.round(probe.height * scale);
            probe.close?.();
            // Request the resized bitmap directly
            const bmp = await window.createImageBitmap(file, {
                resizeWidth: w, resizeHeight: h, resizeQuality: "high",
            });
            const dataUrl = drawToCanvas(bmp, w, h);
            bmp.close?.();
            return { base64: dataUrl.split(",")[1], mime: "image/jpeg", width: w, height: h, size: dataUrl.length };
        } catch (_) {
            // Fall through to the img-tag path
        }
    }

    // Fallback — object URL avoids reading the whole file into a data-URL string
    return await new Promise((resolve, reject) => {
        const url = URL.createObjectURL(file);
        const img = new window.Image();
        img.onload = () => {
            try {
                const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
                const w = Math.round(img.width * scale);
                const h = Math.round(img.height * scale);
                const dataUrl = drawToCanvas(img, w, h);
                URL.revokeObjectURL(url);
                resolve({ base64: dataUrl.split(",")[1], mime: "image/jpeg", width: w, height: h, size: dataUrl.length });
            } catch (err) {
                URL.revokeObjectURL(url);
                reject(err);
            }
        };
        img.onerror = () => {
            URL.revokeObjectURL(url);
            reject(new Error("Could not decode image"));
        };
        img.src = url;
    });
}


/* Photo picker: opens camera on mobile, file dialog on desktop. Downscales locally
 * with createImageBitmap (or object-URL fallback) to keep peak memory low. */
function PhotoPickerButton({ label, testId, onImage, colour = "bg-slate-700 hover:bg-slate-600", maxDim = 1024, quality = 0.7 }) {
    const ref = useRef(null);
    const [busy, setBusy] = useState(false);
    const handle = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setBusy(true);
        try {
            const { base64 } = await resizeImageToBase64(file, maxDim, quality);
            onImage(base64, "image/jpeg");
        } catch (err) {
            toast.error("Could not process the photo — try again with a smaller shot.");
        } finally {
            setBusy(false);
            if (ref.current) ref.current.value = "";
        }
    };
    return (
        <>
            <button
                type="button"
                onClick={() => ref.current?.click()}
                disabled={busy}
                className={`w-full rounded ${colour} disabled:opacity-60 text-white text-xs font-bold py-1.5 flex items-center justify-center gap-1 transition-colors`}
                data-testid={testId}
            >
                <Camera size={14} weight="bold" /> {busy ? "Processing…" : label}
            </button>
            <input ref={ref} type="file" accept="image/*" capture="environment" className="hidden" onChange={handle} data-testid={`${testId}-input`} />
        </>
    );
}


const REASON_CATEGORIES = [
    { v: "blocked_chute",         label: "Blocked chute" },
    { v: "electrical_breakdown",  label: "Electrical breakdown" },
    { v: "mechanical_breakdown",  label: "Mechanical breakdown" },
    { v: "weather",               label: "Weather conditions" },
    { v: "truck_breakdown",       label: "Truck breakdown" },
    { v: "other",                 label: "Other" },
];

function buildRouteSteps(silo) {
    /* Route parts a delay can be attributed to — pulled from the silo's inbound route
     * plus the truck-side stops. */
    const inbound = silo?.inbound_route || [];
    return [
        ...inbound,
        "Truck",
        "Weighbridge",
        "Other / not on inbound route",
    ];
}


function ReasonAndStepFields({ reasonCategory, setReasonCategory, routeStep, setRouteStep, silo, testIdPrefix }) {
    const steps = buildRouteSteps(silo);
    return (
        <div className="grid grid-cols-2 gap-2 mb-3">
            <div>
                <label className="block text-xs font-bold uppercase text-slate-600 mb-1">Reason category</label>
                <select
                    value={reasonCategory}
                    onChange={(e) => setReasonCategory(e.target.value)}
                    className="g-input w-full text-sm"
                    data-testid={`${testIdPrefix}-reason-category`}
                >
                    {REASON_CATEGORIES.map((c) => (
                        <option key={c.v} value={c.v}>{c.label}</option>
                    ))}
                </select>
            </div>
            <div>
                <label className="block text-xs font-bold uppercase text-slate-600 mb-1">Route step affected</label>
                <select
                    value={routeStep}
                    onChange={(e) => setRouteStep(e.target.value)}
                    className="g-input w-full text-sm"
                    data-testid={`${testIdPrefix}-route-step`}
                >
                    <option value="">— Select route step —</option>
                    {steps.map((s) => (
                        <option key={s} value={s}>{s}</option>
                    ))}
                </select>
            </div>
        </div>
    );
}



function TruckArrivalModal({ silo, onClose, onSuccess }) {
    const [truckReg, setTruckReg] = useState("");
    const [slipInNo, setSlipInNo] = useState("");
    const [slipInTons, setSlipInTons] = useState("");
    const [slipInImg, setSlipInImg] = useState(null);
    const [note, setNote] = useState("");
    const [busy, setBusy] = useState(false);
    const [scanning, setScanning] = useState(false);
    const [showLiveScanner, setShowLiveScanner] = useState(false);

    const onScanPlate = async (base64) => {
        setShowLiveScanner(false);
        setScanning(true);
        try {
            const r = await api.post("/tronox/plate-scan", { image_base64: base64 });
            if (r.data.ok) {
                setTruckReg(r.data.plate);
                toast.success(`Plate detected: ${r.data.plate}`);
            } else {
                toast.error(r.data.reason || "Plate could not be read");
            }
        } catch (e) { toast.error(formatApiError(e)); }
        setScanning(false);
    };

    const submit = async () => {
        if (!truckReg.trim()) return toast.error("Truck registration is required");
        const inT = parseFloat(slipInTons);
        if (!inT || inT <= 0) return toast.error("Weighbridge IN slip tons must be > 0");
        setBusy(true);
        try {
            await api.post(`/tronox/silos/${silo.silo_no}/truck-arrive`, {
                truck_registration: truckReg.trim().toUpperCase(),
                slip_in_number: slipInNo.trim(),
                slip_in_tons: inT,
                slip_in_image_base64: slipInImg,
                note: note.trim(),
            });
            toast.success(`${truckReg.toUpperCase()} on-site @ Silo ${silo.silo_no} — clock started`);
            onSuccess();
        } catch (e) { toast.error(formatApiError(e)); }
        setBusy(false);
    };

    return (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-start justify-center p-3 overflow-y-auto" data-testid="truck-arrival-modal">
            <div className="bg-white rounded-lg max-w-lg w-full p-5 shadow-2xl my-5">
                <div className="flex items-start justify-between mb-3">
                    <div>
                        <div className="text-xs uppercase text-slate-500 tracking-wider">Truck arrived — weighed in loaded</div>
                        <h2 className="text-lg font-black text-slate-900">Silo {silo.silo_no} · {silo.product}</h2>
                        <div className="text-xs text-slate-500 mt-0.5">Capture the IN slip to start the delivery window. Silo level updates only when the truck is weighed out empty.</div>
                    </div>
                    <button onClick={onClose} className="text-slate-400 hover:text-slate-800" data-testid="arrival-close"><X size={20} /></button>
                </div>

                <label className="block text-xs font-bold uppercase text-slate-600 mb-1">Truck registration</label>
                <div className="flex gap-2 mb-1">
                    <input value={truckReg} onChange={(e) => setTruckReg(e.target.value.toUpperCase())} placeholder="e.g. ND 123 456" className="g-input flex-1" data-testid="arrival-truck-reg" autoFocus />
                    <button
                        type="button"
                        onClick={() => setShowLiveScanner(true)}
                        disabled={scanning}
                        className="w-40 rounded bg-indigo-600 hover:bg-indigo-500 disabled:opacity-60 text-white text-xs font-bold py-1.5 flex items-center justify-center gap-1 transition-colors"
                        data-testid="arrival-scan-plate"
                    >
                        <Aperture size={14} weight="bold" /> {scanning ? "Reading…" : "Scan plate"}
                    </button>
                </div>
                <div className="text-[10px] text-slate-500 mb-3">Camera opens with a live guide-frame — only the plate area is captured, never a full photo.</div>

                {showLiveScanner && (
                    <LivePlateScanner
                        onCapture={(b64) => onScanPlate(b64)}
                        onCancel={() => setShowLiveScanner(false)}
                    />
                )}

                <div className="rounded border border-slate-200 p-3 mb-3 bg-slate-50">
                    <div className="text-xs font-bold uppercase text-slate-700 mb-2 flex items-center gap-1">
                        <DownloadSimple size={13} weight="bold" className="text-emerald-600" /> Weighbridge IN slip
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                        <div>
                            <label className="block text-[10px] font-bold uppercase text-slate-500 mb-1">Slip number</label>
                            <input value={slipInNo} onChange={(e) => setSlipInNo(e.target.value)} placeholder="WB-0001" className="g-input w-full text-sm" data-testid="arrival-slip-in-number" />
                        </div>
                        <div>
                            <label className="block text-[10px] font-bold uppercase text-slate-500 mb-1">Gross tons</label>
                            <input type="number" step="0.001" value={slipInTons} onChange={(e) => setSlipInTons(e.target.value)} placeholder="34.500" className="g-input w-full text-sm" data-testid="arrival-slip-in-tons" />
                        </div>
                    </div>
                    <div className="mt-2 flex items-center gap-2">
                        <div className="flex-1">
                            <PhotoPickerButton
                                label={slipInImg ? "IN slip captured ✓ — retake" : "Scan IN slip"}
                                testId="arrival-scan-slip-in"
                                onImage={(b64) => setSlipInImg(b64)}
                                colour={slipInImg ? "bg-emerald-600 hover:bg-emerald-500" : "bg-slate-700 hover:bg-slate-600"}
                            />
                        </div>
                        {slipInImg && (
                            <button onClick={() => setSlipInImg(null)} className="text-xs text-slate-500 hover:text-slate-800" data-testid="arrival-clear-slip-in">Clear</button>
                        )}
                    </div>
                </div>

                <label className="block text-xs font-bold uppercase text-slate-600 mb-1">Note (optional)</label>
                <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. driver, seal number" className="g-input w-full mb-3" data-testid="arrival-note" />

                <div className="flex gap-2 justify-end">
                    <button onClick={onClose} className="btn-outline text-sm" data-testid="arrival-cancel" disabled={busy}>Cancel</button>
                    <button onClick={submit} disabled={busy || scanning} className="btn-primary text-sm bg-emerald-600 hover:bg-emerald-500" data-testid="arrival-save">
                        {busy ? "Opening window…" : "Start delivery window"}
                    </button>
                </div>
            </div>
        </div>
    );
}


function TruckCloseModal({ delivery, onClose, onSuccess }) {
    const [slipOutNo, setSlipOutNo] = useState("");
    const [slipOutTons, setSlipOutTons] = useState("");
    const [slipOutImg, setSlipOutImg] = useState(null);
    const [note, setNote] = useState("");
    const [busy, setBusy] = useState(false);

    const netTons = useMemo(() => {
        const inT = parseFloat(delivery.slip_in_tons);
        const outT = parseFloat(slipOutTons);
        if (!inT || inT <= 0) return 0;
        if (isNaN(outT)) return inT;
        return Math.max(0, inT - outT);
    }, [delivery.slip_in_tons, slipOutTons]);

    const submit = async () => {
        const outT = parseFloat(slipOutTons);
        if (isNaN(outT) || outT < 0) return toast.error("Weighbridge OUT tons must be 0 or positive");
        setBusy(true);
        try {
            await api.post(`/tronox/deliveries/${delivery.id}/close`, {
                slip_out_number: slipOutNo.trim(),
                slip_out_tons: outT,
                slip_out_image_base64: slipOutImg,
                note: note.trim(),
            });
            toast.success(`Closed ${delivery.truck_registration} · net ${NUM(netTons)} t → Silo ${delivery.silo_no}`);
            onSuccess();
        } catch (e) { toast.error(formatApiError(e)); }
        setBusy(false);
    };

    return (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-start justify-center p-3 overflow-y-auto" data-testid="truck-close-modal">
            <div className="bg-white rounded-lg max-w-lg w-full p-5 shadow-2xl my-5">
                <div className="flex items-start justify-between mb-3">
                    <div>
                        <div className="text-xs uppercase text-slate-500 tracking-wider">Weigh out & close delivery</div>
                        <h2 className="text-lg font-black text-slate-900">{delivery.truck_registration} · Silo {delivery.silo_no}</h2>
                        <div className="text-xs text-slate-500 mt-0.5">IN slip <b>{delivery.slip_in_number || "—"}</b> · {NUM(delivery.slip_in_tons)} t gross</div>
                    </div>
                    <button onClick={onClose} className="text-slate-400 hover:text-slate-800" data-testid="close-close-btn"><X size={20} /></button>
                </div>

                <div className="rounded border border-slate-200 p-3 mb-3 bg-slate-50">
                    <div className="text-xs font-bold uppercase text-slate-700 mb-2 flex items-center gap-1">
                        <UploadSimple size={13} weight="bold" className="text-sky-600" /> Weighbridge OUT slip
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                        <div>
                            <label className="block text-[10px] font-bold uppercase text-slate-500 mb-1">Slip number</label>
                            <input value={slipOutNo} onChange={(e) => setSlipOutNo(e.target.value)} placeholder="WB-0002" className="g-input w-full text-sm" data-testid="close-slip-out-number" />
                        </div>
                        <div>
                            <label className="block text-[10px] font-bold uppercase text-slate-500 mb-1">Empty tare tons</label>
                            <input type="number" step="0.001" value={slipOutTons} onChange={(e) => setSlipOutTons(e.target.value)} placeholder="0.150" className="g-input w-full text-sm" data-testid="close-slip-out-tons" autoFocus />
                        </div>
                    </div>
                    <div className="mt-2 flex items-center gap-2">
                        <div className="flex-1">
                            <PhotoPickerButton
                                label={slipOutImg ? "OUT slip captured ✓ — retake" : "Scan OUT slip"}
                                testId="close-scan-slip-out"
                                onImage={(b64) => setSlipOutImg(b64)}
                                colour={slipOutImg ? "bg-emerald-600 hover:bg-emerald-500" : "bg-slate-700 hover:bg-slate-600"}
                            />
                        </div>
                        {slipOutImg && (
                            <button onClick={() => setSlipOutImg(null)} className="text-xs text-slate-500 hover:text-slate-800" data-testid="close-clear-slip-out">Clear</button>
                        )}
                    </div>
                </div>

                <div className="flex items-center justify-between rounded bg-emerald-50 border border-emerald-200 px-3 py-2 mb-3">
                    <span className="text-xs font-bold uppercase text-emerald-800">Net tons dropped in silo</span>
                    <span className="text-2xl font-black text-emerald-700" data-testid="close-net-tons">{NUM(netTons)} t</span>
                </div>

                <label className="block text-xs font-bold uppercase text-slate-600 mb-1">Note (optional)</label>
                <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. clean tanker, small residue" className="g-input w-full mb-3" data-testid="close-note" />

                <div className="flex gap-2 justify-end">
                    <button onClick={onClose} className="btn-outline text-sm" data-testid="close-cancel" disabled={busy}>Cancel</button>
                    <button onClick={submit} disabled={busy} className="btn-primary text-sm bg-sky-600 hover:bg-sky-500" data-testid="close-save">
                        {busy ? "Closing…" : "Complete & commit"}
                    </button>
                </div>
            </div>
        </div>
    );
}


function DeliveryPauseModal({ delivery, silo, onClose, onSuccess }) {
    const [reason, setReason] = useState("");
    const [description, setDescription] = useState("");
    const [eta, setEta] = useState("");
    const [category, setCategory] = useState("operations");
    const [reasonCategory, setReasonCategory] = useState("blocked_chute");
    const [routeStep, setRouteStep] = useState("");
    const [busy, setBusy] = useState(false);
    const REASONS = ["Waiting for offload point", "Silo intake blocked", "Bucket elevator issue", "Distribution screw issue", "Product-flow delay", "Truck maintenance", "Weighbridge unavailable", "Other"];
    const submit = async () => {
        if (!reason.trim()) return toast.error("Please select a reason");
        setBusy(true);
        try {
            await api.post(`/tronox/deliveries/${delivery.id}/pause`, {
                reason: reason.trim(),
                description: description.trim(),
                eta_minutes: eta === "" ? null : parseInt(eta, 10),
                category,
                reason_category: reasonCategory,
                route_step: routeStep || null,
            });
            toast.success(`Delivery paused · ${delivery.truck_registration}`);
            onSuccess();
        } catch (e) { toast.error(formatApiError(e)); }
        setBusy(false);
    };
    return (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-3" data-testid="delivery-pause-modal">
            <div className="bg-white rounded-lg max-w-md w-full p-5 shadow-2xl">
                <div className="flex items-start justify-between mb-3">
                    <div>
                        <div className="text-xs uppercase text-slate-500 tracking-wider">Pause truck delivery</div>
                        <h2 className="text-lg font-black text-slate-900">{delivery.truck_registration} · Silo {delivery.silo_no}</h2>
                    </div>
                    <button onClick={onClose} className="text-slate-400 hover:text-slate-800" data-testid="delivery-pause-close"><X size={20} /></button>
                </div>

                <ReasonAndStepFields
                    reasonCategory={reasonCategory}
                    setReasonCategory={setReasonCategory}
                    routeStep={routeStep}
                    setRouteStep={setRouteStep}
                    silo={silo}
                    testIdPrefix="delivery-pause"
                />

                <label className="block text-xs font-bold uppercase text-slate-600 mb-1">Reason</label>
                <select value={reason} onChange={(e) => setReason(e.target.value)} className="g-input w-full mb-3" data-testid="delivery-pause-reason">
                    <option value="">— Select reason —</option>
                    {REASONS.map((r) => <option key={r} value={r}>{r}</option>)}
                </select>
                <label className="block text-xs font-bold uppercase text-slate-600 mb-1">Comments / details</label>
                <textarea rows={2} value={description} onChange={(e) => setDescription(e.target.value)} className="g-input w-full mb-3" placeholder="What happened and what's being done" data-testid="delivery-pause-description" />
                <div className="grid grid-cols-2 gap-2 mb-3">
                    <div>
                        <label className="block text-xs font-bold uppercase text-slate-600 mb-1">ETA (min)</label>
                        <input type="number" min="0" value={eta} onChange={(e) => setEta(e.target.value)} className="g-input w-full" data-testid="delivery-pause-eta" />
                    </div>
                    <div>
                        <label className="block text-xs font-bold uppercase text-slate-600 mb-1">Ops category</label>
                        <select value={category} onChange={(e) => setCategory(e.target.value)} className="g-input w-full" data-testid="delivery-pause-category">
                            <option value="equipment">Equipment</option>
                            <option value="operations">Operations</option>
                            <option value="external">External</option>
                            <option value="documentation">Documentation</option>
                            <option value="other">Other</option>
                        </select>
                    </div>
                </div>
                <div className="flex gap-2 justify-end">
                    <button onClick={onClose} className="btn-outline text-sm" data-testid="delivery-pause-cancel" disabled={busy}>Cancel</button>
                    <button onClick={submit} disabled={busy} className="btn-primary text-sm bg-amber-600 hover:bg-amber-500" data-testid="delivery-pause-submit">{busy ? "Saving…" : "Pause delivery"}</button>
                </div>
            </div>
        </div>
    );
}



function InboundReportDrawer({ onClose }) {
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [sumTons, setSumTons] = useState(0);
    const [loading, setLoading] = useState(true);
    const [q, setQ] = useState("");
    const [siloFilter, setSiloFilter] = useState("all");
    const [selectedImg, setSelectedImg] = useState(null); // { url, title } for slip viewer

    const load = async () => {
        setLoading(true);
        try {
            const r = await api.get("/tronox/deliveries");
            setItems(r.data.deliveries || []);
            setTotal(r.data.total || 0);
            setSumTons(r.data.sum_net_tons || 0);
        } catch (e) { toast.error(formatApiError(e)); }
        setLoading(false);
    };
    useEffect(() => { load(); }, []);

    const viewSlip = async (deliveryId, which, meta) => {
        try {
            const r = await api.get(`/tronox/deliveries/${deliveryId}/slip/${which}`);
            const b64 = r.data.image_base64;
            const url = `data:image/*;base64,${b64}`;
            setSelectedImg({ url, title: `${meta.truck} · ${which.toUpperCase()} slip ${meta.slipNo || "(no number)"} · ${meta.silo}` });
        } catch (e) { toast.error(formatApiError(e) || "No slip on file"); }
    };

    const filtered = useMemo(() => {
        const needle = q.trim().toUpperCase();
        return items.filter((it) => {
            if (siloFilter !== "all" && String(it.silo_no) !== siloFilter) return false;
            if (!needle) return true;
            return (it.truck_registration || "").includes(needle)
                || (it.slip_in_number || "").toUpperCase().includes(needle)
                || (it.slip_out_number || "").toUpperCase().includes(needle);
        });
    }, [items, q, siloFilter]);

    return (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex justify-end" data-testid="inbound-report-drawer">
            <div className="bg-white h-full w-full max-w-3xl overflow-y-auto shadow-2xl">
                <div className="sticky top-0 bg-white border-b border-slate-200 p-4 flex items-center justify-between">
                    <div>
                        <div className="text-xs uppercase text-slate-500 tracking-wider">Tronox · GTRB</div>
                        <h2 className="text-xl font-black text-slate-900 flex items-center gap-2">
                            <Truck size={20} weight="bold" /> Inbound truck deliveries
                        </h2>
                        <div className="text-xs text-slate-500 mt-0.5">
                            {total} deliver{total === 1 ? "y" : "ies"} · <b>{NUM(sumTons)} t</b> total received across all silos
                        </div>
                    </div>
                    <button onClick={onClose} className="text-slate-400 hover:text-slate-800" data-testid="inbound-report-close"><X size={22} /></button>
                </div>

                {/* Filter row */}
                <div className="p-4 flex flex-wrap gap-2 items-center border-b border-slate-100 bg-slate-50">
                    <div className="relative flex-1 min-w-[200px]">
                        <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search truck reg or slip #" className="g-input w-full !pl-10 text-sm" data-testid="inbound-report-search" />
                    </div>
                    <select value={siloFilter} onChange={(e) => setSiloFilter(e.target.value)} className="g-input text-sm !py-1.5" data-testid="inbound-report-silo-filter">
                        <option value="all">All silos</option>
                        {[1,2,3,4,5,6,7,8,9].map((n) => (
                            <option key={n} value={String(n)}>Silo {n}</option>
                        ))}
                    </select>
                </div>

                {loading ? (
                    <div className="p-8 text-center text-slate-500">Loading deliveries…</div>
                ) : filtered.length === 0 ? (
                    <div className="p-8 text-center text-slate-400">
                        <Truck size={40} weight="thin" className="mx-auto mb-2" />
                        No deliveries match your filter. Start recording truck deliveries via the RECEIVE button on any silo.
                    </div>
                ) : (
                    <table className="w-full text-sm" data-testid="inbound-report-table">
                        <thead className="text-[10px] uppercase text-slate-500 tracking-wider bg-slate-100 sticky top-[128px]">
                            <tr>
                                <th className="text-left px-3 py-2">When</th>
                                <th className="text-left px-3 py-2">Silo</th>
                                <th className="text-left px-3 py-2">Truck</th>
                                <th className="text-left px-3 py-2">Slip IN</th>
                                <th className="text-left px-3 py-2">Slip OUT</th>
                                <th className="text-right px-3 py-2">Net t</th>
                                <th className="text-left px-3 py-2">By</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map((d) => (
                                <tr key={d.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`inbound-row-${d.id}`}>
                                    <td className="px-3 py-2 text-slate-600 text-xs whitespace-nowrap">
                                        {new Date(d.recorded_at).toLocaleString(undefined, { day:"2-digit", month:"short", hour:"2-digit", minute:"2-digit" })}
                                    </td>
                                    <td className="px-3 py-2 font-bold">
                                        <span className="inline-block px-2 py-0.5 rounded bg-slate-100 text-slate-800 text-xs">Silo {d.silo_no}</span>
                                        <span className="ml-1 text-[10px] text-slate-500">{d.product}</span>
                                    </td>
                                    <td className="px-3 py-2 font-mono font-bold text-slate-900">{d.truck_registration}</td>
                                    <td className="px-3 py-2 text-xs">
                                        <div className="flex items-center gap-1">
                                            <span className="font-mono">{d.slip_in_number || "—"}</span>
                                            <span className="text-slate-500">· {NUM(d.slip_in_tons)} t</span>
                                            {d.has_slip_in_image && (
                                                <button onClick={() => viewSlip(d.id, "in", { truck: d.truck_registration, slipNo: d.slip_in_number, silo: `Silo ${d.silo_no}` })} className="text-indigo-600 hover:text-indigo-800" title="View slip photo" data-testid={`view-slip-in-${d.id}`}>
                                                    <ImageIcon size={14} />
                                                </button>
                                            )}
                                        </div>
                                    </td>
                                    <td className="px-3 py-2 text-xs">
                                        {d.slip_out_tons !== null && d.slip_out_tons !== undefined ? (
                                            <div className="flex items-center gap-1">
                                                <span className="font-mono">{d.slip_out_number || "—"}</span>
                                                <span className="text-slate-500">· {NUM(d.slip_out_tons)} t</span>
                                                {d.has_slip_out_image && (
                                                    <button onClick={() => viewSlip(d.id, "out", { truck: d.truck_registration, slipNo: d.slip_out_number, silo: `Silo ${d.silo_no}` })} className="text-indigo-600 hover:text-indigo-800" title="View slip photo" data-testid={`view-slip-out-${d.id}`}>
                                                        <ImageIcon size={14} />
                                                    </button>
                                                )}
                                            </div>
                                        ) : (
                                            <span className="text-slate-400 italic text-xs">not weighed out</span>
                                        )}
                                    </td>
                                    <td className="px-3 py-2 text-right font-black text-emerald-700 whitespace-nowrap">{NUM(d.net_tons)} t</td>
                                    <td className="px-3 py-2 text-xs text-slate-500">{d.recorded_by_name || "—"}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}

                {/* Slip image viewer overlay */}
                {selectedImg && (
                    <div className="fixed inset-0 z-[60] bg-black/80 flex items-center justify-center p-4" onClick={() => setSelectedImg(null)} data-testid="slip-viewer">
                        <div className="bg-white rounded max-w-3xl w-full p-3 max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
                            <div className="flex items-center justify-between mb-2 pb-2 border-b border-slate-100">
                                <div className="text-sm font-bold text-slate-900">{selectedImg.title}</div>
                                <button onClick={() => setSelectedImg(null)} className="text-slate-400 hover:text-slate-800" data-testid="slip-viewer-close"><X size={20} /></button>
                            </div>
                            <img src={selectedImg.url} alt="weighbridge slip" className="max-w-full h-auto rounded" />
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}


function SiloPauseModal({ silo, onClose, onSuccess }) {
    const isUpdate = !!silo.pause_state;
    const [reason, setReason] = useState(silo.pause_state?.reason || "");
    const [description, setDescription] = useState(silo.pause_state?.description || "");
    const [eta, setEta] = useState(silo.pause_state?.eta_minutes ?? "");
    const [category, setCategory] = useState(silo.pause_state?.category || "equipment");
    const [accountability, setAccountability] = useState(silo.pause_state?.accountability || "grindrod");
    const [reasonCategory, setReasonCategory] = useState(silo.pause_state?.reason_category || "mechanical_breakdown");
    const [routeStep, setRouteStep] = useState(silo.pause_state?.route_step || "");
    const [busy, setBusy] = useState(false);

    const REASONS = [
        "Equipment breakdown",
        "Bucket elevator failure",
        "Distribution screw failure",
        "Conveyor tripped",
        "Waiting for trucks",
        "Weighbridge unavailable",
        "Weather (rain / wind)",
        "Product / spec issue",
        "Housekeeping",
        "Shift change",
        "Other",
    ];

    const submit = async () => {
        if (!reason.trim()) return toast.error("Please pick a reason");
        setBusy(true);
        try {
            const payload = {
                reason: reason.trim(),
                description: description.trim(),
                eta_minutes: eta === "" ? null : parseInt(eta, 10),
                category, accountability,
                reason_category: reasonCategory,
                route_step: routeStep || null,
            };
            if (isUpdate) {
                await api.patch(`/tronox/silos/${silo.silo_no}/pause`, payload);
                toast.success(`Silo ${silo.silo_no} pause updated`);
            } else {
                await api.post(`/tronox/silos/${silo.silo_no}/pause`, payload);
                toast.success(`Silo ${silo.silo_no} paused${accountability === "grindrod" ? " · millwrights alerted" : ""}`);
            }
            onSuccess();
        } catch (e) { toast.error(formatApiError(e)); }
        setBusy(false);
    };

    return (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-3" data-testid="silo-pause-modal">
            <div className="bg-white rounded-lg max-w-md w-full p-5 shadow-2xl">
                <div className="flex items-start justify-between mb-3">
                    <div>
                        <div className="text-xs uppercase text-slate-500 tracking-wider">{isUpdate ? "Update pause" : "Pause silo receiving"}</div>
                        <h2 className="text-lg font-black text-slate-900">Silo {silo.silo_no} · {silo.product}</h2>
                    </div>
                    <button onClick={onClose} className="text-slate-400 hover:text-slate-800" data-testid="silo-pause-close"><X size={20} /></button>
                </div>

                <ReasonAndStepFields
                    reasonCategory={reasonCategory}
                    setReasonCategory={setReasonCategory}
                    routeStep={routeStep}
                    setRouteStep={setRouteStep}
                    silo={silo}
                    testIdPrefix="silo-pause"
                />

                <label className="block text-xs font-bold uppercase text-slate-600 mb-1">Reason</label>
                <select value={reason} onChange={(e) => setReason(e.target.value)} className="g-input w-full mb-3" data-testid="silo-pause-reason">
                    <option value="">— Select reason —</option>
                    {REASONS.map((r) => <option key={r} value={r}>{r}</option>)}
                </select>

                <label className="block text-xs font-bold uppercase text-slate-600 mb-1">Description / action taken</label>
                <textarea rows={2} value={description} onChange={(e) => setDescription(e.target.value)} className="g-input w-full mb-3" placeholder="What happened, what's being done" data-testid="silo-pause-description" />

                <div className="grid grid-cols-2 gap-2 mb-3">
                    <div>
                        <label className="block text-xs font-bold uppercase text-slate-600 mb-1">ETA (min)</label>
                        <input type="number" min="0" value={eta} onChange={(e) => setEta(e.target.value)} className="g-input w-full" placeholder="e.g. 30" data-testid="silo-pause-eta" />
                    </div>
                    <div>
                        <label className="block text-xs font-bold uppercase text-slate-600 mb-1">Category</label>
                        <select value={category} onChange={(e) => setCategory(e.target.value)} className="g-input w-full" data-testid="silo-pause-category">
                            <option value="equipment">Equipment</option>
                            <option value="external">External</option>
                            <option value="operations">Operations</option>
                            <option value="documentation">Documentation</option>
                            <option value="other">Other</option>
                        </select>
                    </div>
                </div>

                <label className="block text-xs font-bold uppercase text-slate-600 mb-1">Accountability</label>
                <div className="grid grid-cols-4 gap-1 mb-3">
                    {[
                        { v: "grindrod", label: "Grindrod" },
                        { v: "client",   label: "Client" },
                        { v: "transnet", label: "Transnet" },
                        { v: "weather",  label: "Weather" },
                    ].map((opt) => (
                        <button
                            key={opt.v}
                            type="button"
                            onClick={() => setAccountability(opt.v)}
                            className={`text-[11px] font-bold py-1.5 rounded border transition ${accountability === opt.v ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-300 hover:border-slate-500"}`}
                            data-testid={`silo-pause-account-${opt.v}`}
                        >
                            {opt.label}
                        </button>
                    ))}
                </div>
                {accountability === "grindrod" && !isUpdate && (
                    <div className="text-[11px] rounded bg-rose-50 text-rose-800 border border-rose-200 px-2 py-1 mb-3">
                        A millwright breakdown notification will be raised — the maintenance team will see this immediately with an accept / ETA / feedback flow, exactly like on the vessel side.
                    </div>
                )}

                <div className="flex gap-2 justify-end">
                    <button onClick={onClose} className="btn-outline text-sm" data-testid="silo-pause-cancel" disabled={busy}>Cancel</button>
                    <button onClick={submit} disabled={busy} className="btn-primary text-sm bg-amber-600 hover:bg-amber-500" data-testid="silo-pause-submit">
                        {busy ? "Saving…" : (isUpdate ? "Update pause" : "Pause silo")}
                    </button>
                </div>
            </div>
        </div>
    );
}


function SiloAdhocDelayModal({ silo, onClose, onSuccess }) {
    const now = new Date();
    const iso = (d) => new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
    const [start, setStart] = useState(iso(new Date(now.getTime() - 30 * 60000)));
    const [end, setEnd] = useState(iso(now));
    const [reason, setReason] = useState("");
    const [actions, setActions] = useState("");
    const [category, setCategory] = useState("external");
    const [reasonCategory, setReasonCategory] = useState("blocked_chute");
    const [routeStep, setRouteStep] = useState("");
    const [busy, setBusy] = useState(false);

    const submit = async () => {
        if (!reason.trim()) return toast.error("Reason is required");
        if (!start) return toast.error("Start time is required");
        setBusy(true);
        try {
            await api.post(`/tronox/silos/${silo.silo_no}/delays`, {
                start_time: new Date(start).toISOString(),
                end_time: end ? new Date(end).toISOString() : null,
                reason: reason.trim(),
                actions_taken: actions.trim(),
                category,
                reason_category: reasonCategory,
                route_step: routeStep || null,
            });
            toast.success(`Delay logged against Silo ${silo.silo_no}`);
            onSuccess();
        } catch (e) { toast.error(formatApiError(e)); }
        setBusy(false);
    };

    return (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-3" data-testid="silo-adhoc-delay-modal">
            <div className="bg-white rounded-lg max-w-md w-full p-5 shadow-2xl">
                <div className="flex items-start justify-between mb-3">
                    <div>
                        <div className="text-xs uppercase text-slate-500 tracking-wider">Log an ad-hoc delay</div>
                        <h2 className="text-lg font-black text-slate-900">Silo {silo.silo_no} · {silo.product}</h2>
                        <div className="text-xs text-slate-500">Record a delay after the fact — e.g. trucks that had to queue at the weighbridge.</div>
                    </div>
                    <button onClick={onClose} className="text-slate-400 hover:text-slate-800" data-testid="silo-adhoc-close"><X size={20} /></button>
                </div>
                <ReasonAndStepFields
                    reasonCategory={reasonCategory}
                    setReasonCategory={setReasonCategory}
                    routeStep={routeStep}
                    setRouteStep={setRouteStep}
                    silo={silo}
                    testIdPrefix="silo-adhoc"
                />
                <div className="grid grid-cols-2 gap-2 mb-3">
                    <div>
                        <label className="block text-xs font-bold uppercase text-slate-600 mb-1">Start</label>
                        <input type="datetime-local" value={start} onChange={(e) => setStart(e.target.value)} className="g-input w-full" data-testid="silo-adhoc-start" />
                    </div>
                    <div>
                        <label className="block text-xs font-bold uppercase text-slate-600 mb-1">End</label>
                        <input type="datetime-local" value={end} onChange={(e) => setEnd(e.target.value)} className="g-input w-full" data-testid="silo-adhoc-end" />
                    </div>
                </div>
                <label className="block text-xs font-bold uppercase text-slate-600 mb-1">Reason (short)</label>
                <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Trucks queued at weighbridge" className="g-input w-full mb-3" data-testid="silo-adhoc-reason" />
                <label className="block text-xs font-bold uppercase text-slate-600 mb-1">Comments / actions taken</label>
                <textarea rows={2} value={actions} onChange={(e) => setActions(e.target.value)} className="g-input w-full mb-3" data-testid="silo-adhoc-actions" />
                <label className="block text-xs font-bold uppercase text-slate-600 mb-1">Ops category</label>
                <select value={category} onChange={(e) => setCategory(e.target.value)} className="g-input w-full mb-3" data-testid="silo-adhoc-category">
                    <option value="equipment">Equipment</option>
                    <option value="external">External</option>
                    <option value="operations">Operations</option>
                    <option value="documentation">Documentation</option>
                    <option value="other">Other</option>
                </select>
                <div className="flex gap-2 justify-end">
                    <button onClick={onClose} className="btn-outline text-sm" data-testid="silo-adhoc-cancel" disabled={busy}>Cancel</button>
                    <button onClick={submit} disabled={busy} className="btn-primary text-sm" data-testid="silo-adhoc-submit">{busy ? "Saving…" : "Log delay"}</button>
                </div>
            </div>
        </div>
    );
}


export function InboundSilosView() {
    const { user } = useAuth();
    const [data, setData] = useState({ silos: [], total_capacity_tons: 0, total_current_tons: 0 });
    const [loading, setLoading] = useState(true);
    const [modal, setModal] = useState(null); // { kind, silo, session? }
    const [showReport, setShowReport] = useState(false);
    const [now, setNow] = useState(fmtDT());
    const pollRef = useRef(null);

    const canConfigure = ["admin", "manager"].includes(user?.role);
    const canOperate = ["admin", "supervisor"].includes(user?.role);

    const load = async () => {
        try {
            const r = await api.get("/tronox/silos");
            setData(r.data);
        } catch (e) { toast.error(formatApiError(e)); }
        setLoading(false);
    };

    useEffect(() => {
        load();
        pollRef.current = setInterval(load, 5000);
        const clockId = setInterval(() => setNow(fmtDT()), 1000);
        return () => {
            if (pollRef.current) clearInterval(pollRef.current);
            clearInterval(clockId);
        };
    }, []);

    const onReceiveClicked = (silo) => {
        // Two-stage flow: opens the "TRUCK IN" arrival modal.
        // Step 2 (weigh-out & close) is triggered from the live truck row on the card.
        setModal({ kind: "arrival", silo });
    };

    const onDeliveryPauseClicked = (delivery) => {
        // Find the silo so we can populate the "route step" dropdown
        const silo = data.silos.find((s) => s.silo_no === delivery.silo_no);
        setModal({ kind: "deliveryPause", delivery, silo });
    };
    const onDeliveryCloseClicked = (delivery) => setModal({ kind: "deliveryClose", delivery });
    const onDeliveryResumeClicked = async (delivery) => {
        try {
            await api.post(`/tronox/deliveries/${delivery.id}/resume`, {});
            toast.success(`Delivery resumed · ${delivery.truck_registration}`);
            load();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const closeModal = () => setModal(null);
    const refresh = () => { closeModal(); load(); };

    const totalPct = data.total_capacity_tons ? (data.total_current_tons / data.total_capacity_tons * 100).toFixed(1) : "0.0";

    return (
        <div className="rounded-xl bg-slate-950 text-slate-100 p-5 shadow-inner" data-testid="tronox-silo-dashboard">
            {/* Header row */}
            <div className="flex flex-wrap items-center justify-between gap-3 pb-4 border-b border-slate-800">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-teal-500/15 border border-teal-500/40 flex items-center justify-center">
                        <ChartBar size={20} className="text-teal-300" />
                    </div>
                    <div>
                        <div className="text-[11px] uppercase tracking-widest text-slate-400">Tronox · GTRB Terminal</div>
                        <div className="text-2xl font-black tracking-tight">Silo Storage Overview</div>
                    </div>
                </div>
                <div className="flex items-center gap-3">
                    <button
                        onClick={() => setShowReport(true)}
                        className="inline-flex items-center gap-2 px-3 py-2 rounded bg-indigo-600 hover:bg-indigo-500 text-white text-sm font-bold transition-colors"
                        data-testid="open-inbound-report"
                    >
                        <ListChecks size={16} weight="bold" /> Inbound data
                    </button>
                    <div className="text-right text-xs text-slate-400">
                        <div className="font-mono">{now}</div>
                        <div className="flex items-center gap-1 justify-end mt-0.5">
                            <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                            <span className="text-emerald-400 font-bold tracking-wider">LIVE</span>
                        </div>
                    </div>
                </div>
            </div>

            {loading ? (
                <div className="text-center text-slate-400 py-10" data-testid="tronox-loading">Loading silos…</div>
            ) : (
                <>
                    {/* Grid of silos — 3 columns on desktop, 2 on tablet, 1 on mobile */}
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 mt-4" data-testid="silo-grid">
                        {data.silos.map((s) => (
                            <SiloCard
                                key={s.silo_no}
                                silo={s}
                                canConfigure={canConfigure}
                                canOperate={canOperate}
                                onReceive={onReceiveClicked}
                                onConfigure={(silo) => setModal({ kind: "configure", silo })}
                                onDeliveryPause={onDeliveryPauseClicked}
                                onDeliveryResume={onDeliveryResumeClicked}
                                onDeliveryClose={onDeliveryCloseClicked}
                            />
                        ))}
                    </div>

                    {/* Footer strip */}
                    <div className="mt-5 pt-4 border-t border-slate-800 flex flex-wrap items-center justify-between gap-3 text-sm">
                        <div className="text-[11px] text-slate-500 flex items-center gap-1">
                            <Info size={12} /> All tonnages in metric tons (t). Levels refresh every 5 seconds.
                        </div>
                        <div className="flex items-center gap-2">
                            <span className="text-[11px] uppercase tracking-wider text-slate-400">On hand</span>
                            <span className="text-lg font-black text-emerald-300" data-testid="tronox-total-current">{NUM(data.total_current_tons)} t</span>
                            <span className="text-xs text-slate-500">({totalPct}%)</span>
                        </div>
                    </div>

                    <div className="mt-4 text-[11px] text-amber-300/80 bg-amber-500/10 border border-amber-500/30 rounded p-2 flex items-start gap-2">
                        <Warning size={14} className="mt-[2px] flex-shrink-0" />
                        <div>
                            <b>Shared diverter</b> — Infeed 6 and the Silo 6 bucket elevator are shared between silos 6, 7 and 8 via the diverter box. Only one of these three silos may receive at a time.
                        </div>
                    </div>
                </>
            )}

            {/* Modals */}
            {modal?.kind === "arrival" && (
                <TruckArrivalModal silo={modal.silo} onClose={closeModal} onSuccess={refresh} />
            )}
            {modal?.kind === "deliveryClose" && (
                <TruckCloseModal delivery={modal.delivery} onClose={closeModal} onSuccess={refresh} />
            )}
            {modal?.kind === "deliveryPause" && (
                <DeliveryPauseModal delivery={modal.delivery} silo={modal.silo} onClose={closeModal} onSuccess={refresh} />
            )}
            {modal?.kind === "configure" && (
                <ConfigureSiloModal silo={modal.silo} onClose={closeModal} onSuccess={refresh} />
            )}
            {modal?.kind === "pause" && (
                <SiloPauseModal silo={modal.silo} onClose={closeModal} onSuccess={refresh} />
            )}
            {modal?.kind === "adhocDelay" && (
                <SiloAdhocDelayModal silo={modal.silo} onClose={closeModal} onSuccess={refresh} />
            )}
            {showReport && (
                <InboundReportDrawer onClose={() => setShowReport(false)} />
            )}
        </div>
    );
}



/* ═══════════════════════════════════════════════════════════════════════════
 * Phase 2 wrapper — Main Dashboard + Inbound / Outbound sub-tabs.
 *
 * The Tronox tab in Portal.jsx renders this default export. It contains three
 * sub-views:
 *   1. Main Dashboard  — active operations + upcoming GTRB vessels + Start Vessel
 *   2. Inbound Silos   — the full silo grid (existing UI) for receiving trucks
 *   3. Outbound Silos  — the same silo grid, framed for outbound export supervision
 * ═══════════════════════════════════════════════════════════════════════════ */

const TRONOX_SUBTABS = [
    { key: "main",     label: "Main Dashboard",  short: "Main" },
    { key: "inbound",  label: "Inbound Silos",   short: "Inbound" },
    { key: "outbound", label: "Outbound Silos",  short: "Outbound" },
];

export default function TronoxSiloDashboard() {
    const [sub, setSub] = useState("main");

    return (
        <div className="space-y-3" data-testid="tronox-tab-root">
            {/* Sub-tab bar */}
            <div className="flex flex-nowrap overflow-x-auto gap-1 p-1 bg-slate-100 rounded-md" data-testid="tronox-subtabs">
                {TRONOX_SUBTABS.map((t) => {
                    const active = sub === t.key;
                    return (
                        <button
                            key={t.key}
                            onClick={() => setSub(t.key)}
                            className={`px-3 py-1.5 rounded text-xs sm:text-sm font-bold whitespace-nowrap transition-colors ${active ? "bg-white text-slate-900 shadow-sm" : "text-slate-600 hover:text-slate-900 hover:bg-white/60"}`}
                            data-testid={`tronox-subtab-${t.key}`}
                        >
                            {t.label}
                        </button>
                    );
                })}
            </div>

            {sub === "main" && (
                <MainDashboard
                    onOpenInbound={() => setSub("inbound")}
                    onOpenOutbound={() => setSub("outbound")}
                />
            )}
            {sub === "inbound" && (
                <div data-testid="tronox-inbound-view">
                    <InboundSilosView />
                </div>
            )}
            {sub === "outbound" && (
                <div data-testid="tronox-outbound-view">
                    <div className="mb-3 rounded-lg border border-sky-500/40 bg-sky-950/50 p-3 text-xs text-sky-200" data-testid="outbound-header-banner">
                        <b className="text-sky-100">Outbound Silos</b> — silo levels shown below are live and drop automatically as outbound loads are logged from the Load-Out Station. Use the Main Dashboard&apos;s <i>Start Vessel</i> to begin an export operation.
                    </div>
                    <InboundSilosView />
                </div>
            )}
        </div>
    );
}
